/* eslint-disable */
import React, { Suspense, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
    BrowserRouter,
    Route,
    Switch,
    Redirect,
    useLocation,
} from 'react-router-dom'
import { connect } from 'react-redux'
import { getCurrentUser as getCurrentUserRedux } from './redux'
import TopLoader from './components/commons/TopLoader'
import TuringUserValidation from './components/forms/TusingUserValidation.jsx'
import MainLayout from './components/layout/MainLayout'
import NotFound from './components/commons/NotFound.jsx'

function ScrollToTop() {
    const { pathname } = useLocation()
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [pathname])
    return null
}
const CalendarPage = React.lazy(() => import('./components/CalendarPage'))

// 💤 Lazy-load all route components
const NavBar = React.lazy(() => import('./components/commons/NavBar'))
const FloatingButton = React.lazy(() =>
    import('./components/commons/FloatingButton')
)
const LandingPage = React.lazy(() => import('./components/LandingPage'))
const Login = React.lazy(() => import('./components/Login'))
const Register = React.lazy(() => import('./components/Register'))
const Careers = React.lazy(() => import('./components/Careers'))
const GradeAnalyzer = React.lazy(() => import('./components/GradeAnalyzer'))
const VerifyUser = React.lazy(() => import('./components/VerifyUser'))
const GenerateResetPasswordEmail = React.lazy(() =>
    import('./components/GenerateResetPasswordEmail')
)
const ResetPassword = React.lazy(() => import('./components/ResetPassword'))
const ContactUs = React.lazy(() => import('./components/ContactUs'))
const PrivacyPolicy = React.lazy(() =>
    import('./components/commons/PrivacyPolicy')
)
const TermsOfService = React.lazy(() =>
    import('./components/commons/TermsOfService')
)
const AdminPanel = React.lazy(() => import('./components/AdminPanel.jsx'))
const MyNotesPage = React.lazy(() => import('./components/notes/MyNotesPage'))
const NotePage = React.lazy(() => import('./components/notes/NotePage'))
const PublicNotePage = React.lazy(() =>
    import('./components/notes/PublicNotePage')
)

// Profile
const Profile = React.lazy(() => import('./v3/pages/ProfilePage.jsx'))
const ProfilePageOthers = React.lazy(() =>
    import('./v3/pages/ProfilePageOthers.jsx')
)
const PerformanceAnalyticsPage = React.lazy(() =>
    import('./components/analytics/PerformanceAnalyticsPage.jsx')
)
const VirtualTAPage = React.lazy(() => import('./v3/pages/VirtualTAPage.jsx'))
const Organizations = React.lazy(() =>
    import('./components/courses-new/Organizations.jsx')
)

// Quiz + Courses
const Courses = React.lazy(() => import('./components/courses-new/Courses'))
const Course = React.lazy(() => import('./components/courses-new/Course.jsx'))
const Assignment = React.lazy(() =>
    import(
        './components/courses-new/tabs/assignments/Assignment/Assignment.jsx'
    )
)
const QuizPageV2 = React.lazy(() => import('./components/quiz-new/Quiz.jsx'))
const QuizJoinPage = React.lazy(() => import('./v3/pages/QuizPage.jsx'))
const QuizPageV3 = React.lazy(() => import('./v3/pages/QuizPage.jsx'))
const QuizDemoPage = React.lazy(() => import('./v3/pages/QuizDemoPage.jsx'))
const CreateQuiz = React.lazy(() => import('./components/quiz-new/CreateQuiz'))
const QuizCategories = React.lazy(() =>
    import('./components/quiz/QuizCategories')
)
const UploadQuizQuestion = React.lazy(() =>
    import('./components/quiz/uploadQuizQuestions.jsx')
)
const QuizInstructorPage = React.lazy(() =>
    import('./v3/pages/QuizInstructorPage.jsx')
)
const QuestionCreatorDashboard = React.lazy(() =>
    import('./v3/components/questionmanagement/QuestionCreatorDashboard.jsx')
)

// Canvas
const CanvasRequestAccess = React.lazy(() =>
    import('./components/canvas/CanvasRequestAccess')
)
const CourseCards = React.lazy(() => import('./components/canvas/CoursesInfo'))
const CourseDetails = React.lazy(() =>
    import('./components/canvas/CourseDetails')
)
const Home = React.lazy(() => import('./components/canvas/CanvasCoursHome'))
const Modules = React.lazy(() =>
    import('./components/canvas/CanvasCourseModules')
)
const CanvasQuiz = React.lazy(() => import('./components/canvas/CanvasQuiz'))
const CanvasAssignments = React.lazy(() =>
    import('./components/canvas/canvasCourseAssignments.jsx')
)
const CanvasCreateQuiz = React.lazy(() =>
    import('./components/canvas/CanvasCreateQuiz')
)

// Misc
const MyAccountForm = React.lazy(() => import('./components/MyAccountForm'))
const CoursesList = React.lazy(() => import('./components/courses/CoursesList'))
const CoursesHome = React.lazy(() => import('./components/courses/CoursesHome'))
const GoogleMeetCallback = React.lazy(() =>
    import('./components/GoogleMeetCallback')
)

function Routes({ userData, getCurrentUser }) {
    const [hideTA, setHideTA] = useState(false)
    const [hideNav, setHideNav] = useState(false)

    useEffect(() => {
        if (!userData.user || userData.user.id === undefined) {
            getCurrentUser()
        }
    }, [userData.user, getCurrentUser])

    useEffect(() => {
        const handleToggle = (e) => {
            setHideNav(e.detail?.hideNav || false)
            setHideTA(e.detail?.hideTA || false)
            if (e.detail?.hideNav) {
                document.body.style.paddingTop = '0px'
            } else {
                document.body.style.paddingTop = ''
            }
        }
        window.addEventListener('toggle-quiz-ui', handleToggle)
        return () => {
            window.removeEventListener('toggle-quiz-ui', handleToggle)
        }
    }, [])

    const isProd = process.env.REACT_APP_PROD === 'true'
    const isInviteRoute = window.location.pathname === '/quiz/join'
    const [isValidated, setIsValidated] = useState(() =>
        isProd || isInviteRoute ? true : localStorage.getItem('validated')
    )

    const userRole = userData?.user?.role || 'Student'

    return (
        <>
            {userData.loading && (
                <TopLoader message={userData.loadingMessage} />
            )}
            <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<TopLoader message="Loading..." />}>
                    <MainLayout
                        hideNav={hideNav}
                        hideTA={hideTA}
                        isValidated={isValidated}
                    >
                        {/* Main App Switch Routes */}
                        {!userData.userFetchCompleted ? null : isValidated ||
                          userData === undefined ? (
                            <Switch>
                                <Route exact path="/" component={LandingPage} />
                                <Route
                                    exact
                                    path="/api/v2/auth/google-meet/callback"
                                    component={GoogleMeetCallback}
                                />
                                <Route
                                    exact
                                    path="/calendar"
                                    component={CalendarPage}
                                />
                                <Route
                                    exact
                                    path="/contact-us"
                                    component={ContactUs}
                                />
                                <Route
                                    exact
                                    path="/privacy-policy"
                                    component={PrivacyPolicy}
                                />
                                <Route
                                    exact
                                    path="/terms-of-service"
                                    component={TermsOfService}
                                />
                                <Route
                                    exact
                                    path="/display-account/:email"
                                    component={ProfilePageOthers}
                                />
                                <Route
                                    exact
                                    path="/careers"
                                    component={Careers}
                                />
                                <Route
                                    exact
                                    path="/grade-analyzer"
                                    component={GradeAnalyzer}
                                />
                                <Route
                                    exact
                                    path="/grade-analyzer/:analyzer_course_id"
                                    component={GradeAnalyzer}
                                />
                                <Route exact path="/login" component={Login} />
                                <Route
                                    exact
                                    path="/register"
                                    component={Register}
                                />
                                <Route
                                    exact
                                    path="/verify-user"
                                    component={VerifyUser}
                                />
                                <Route
                                    exact
                                    path="/admin-panel"
                                    component={AdminPanel}
                                />
                                <Route
                                    exact
                                    path="/question-creator-dashboard"
                                    component={QuestionCreatorDashboard}
                                />
                                <Route
                                    exact
                                    path="/reset-password/generate"
                                    component={GenerateResetPasswordEmail}
                                />
                                <Route
                                    exact
                                    path="/reset-password"
                                    component={ResetPassword}
                                />
                                <Route
                                    exact
                                    path="/courses"
                                    component={Courses}
                                />
                                <Route
                                    exact
                                    path="/course/:id"
                                    component={Course}
                                />
                                <Route
                                    exact
                                    path="/quiz/categories"
                                    component={QuizCategories}
                                />
                                <Route
                                    exact
                                    path="/quiz/create-quiz"
                                    component={CreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/quiz/manage/:courseId"
                                    component={CreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/manageQuestions"
                                    component={UploadQuizQuestion}
                                />
                                <Route
                                    exact
                                    path="/quiz/v2/:quiz_id"
                                    component={QuizPageV2}
                                />
                                <Route
                                    exact
                                    path="/quiz/v3/:quiz_id"
                                    component={QuizPageV3}
                                />
                                <Route
                                    exact
                                    path="/quiz/iesmarvo/demo"
                                    component={QuizDemoPage}
                                />

                                <Route
                                    exact
                                    path="/quiz/instructor/:course_id/:quiz_id"
                                    component={QuizInstructorPage}
                                />
                                <Route
                                    exact
                                    path="/course/:id/assignment/:assignment_id"
                                    component={Assignment}
                                />
                                <Route
                                    exact
                                    path="/canvas"
                                    component={CanvasRequestAccess}
                                />
                                <Route
                                    exact
                                    path="/myprofile"
                                    component={Profile}
                                />
                                <Route
                                    exact
                                    path="/profile"
                                    component={Profile}
                                />
                                <Route
                                    exact
                                    path="/profile/analytics"
                                    component={PerformanceAnalyticsPage}
                                />
                                <Route
                                    exact
                                    path="/analytics"
                                    component={PerformanceAnalyticsPage}
                                />
                                <Route
                                    exact
                                    path="/courses/:courseId/analytics"
                                    component={PerformanceAnalyticsPage}
                                />
                                <Route
                                    exact
                                    path="/org"
                                    component={Organizations}
                                />
                                <Route
                                    exact
                                    path="/virtual-ta"
                                    component={VirtualTAPage}
                                />
                                <Route
                                    exact
                                    path="/notes"
                                    component={MyNotesPage}
                                />
                                <Route
                                    exact
                                    path="/note/:id"
                                    component={NotePage}
                                />
                                <Route
                                    exact
                                    path="/course/:courseId/public-note/:id"
                                    render={(props) => (
                                        <PublicNotePage
                                            {...props}
                                            role={userRole}
                                        />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/upgrade-form/:user"
                                    component={MyAccountForm}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas"
                                    component={CourseCards}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id"
                                    component={CourseDetails}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/home"
                                    component={Home}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/modules"
                                    component={Modules}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/quiz"
                                    component={CanvasQuiz}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/assignments"
                                    component={CanvasAssignments}
                                />
                                <Route
                                    exact
                                    path="/v1/courses"
                                    component={CoursesList}
                                />
                                <Route
                                    exact
                                    path="/v1/course/home/:course_id"
                                    component={CoursesHome}
                                />
                                <Route
                                    exact
                                    path="/v1/courses/canvas/:course_id/quiz/edit"
                                    component={CanvasCreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/v1/course/:course_id"
                                    component={GradeAnalyzer}
                                />
                                <Route
                                    exact
                                    path="/quiz/join"
                                    component={QuizJoinPage}
                                />
                                <Route component={NotFound} />
                            </Switch>
                        ) : (
                            <Switch>
                                <Route
                                    path="/validate"
                                    render={() => (
                                        <TuringUserValidation
                                            onValidate={() =>
                                                setIsValidated(true)
                                            }
                                        />
                                    )}
                                />
                                <Route
                                    render={() => <Redirect to="/validate" />}
                                />
                            </Switch>
                        )}
                    </MainLayout>
                </Suspense>
            </BrowserRouter>
        </>
    )
}

Routes.propTypes = {
    userData: PropTypes.shape({
        loading: PropTypes.bool,
        loadingMessage: PropTypes.string,
        user: PropTypes.shape({
            id: PropTypes.string,
        }),
    }).isRequired,
    getCurrentUser: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => ({
    userData: state.userData,
})

const mapDispatchToProps = (dispatch) => ({
    getCurrentUser: () => dispatch(getCurrentUserRedux()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Routes)
